import { Link, NumberField } from '@flos/react-ui';
import type { OPCTermDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useState } from 'react';
import { useHippoText } from '../../../../hooks/useHippoText';
import { isOpcDataFilledAtom } from '../../../../recoil/atoms/isOpcDataFilledAtom';
import { getCurrencyString } from '../../../../utils/currencyUtil';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { infoModalVariantOpcAtom } from '../../../../recoil/atoms/infoModalVariantOpcAtom';
import {
    ACC_DEATH_COVERAGE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
} from '../../../../constants/term';
import { callMeVariantOpcAtom } from '../../../../recoil/atoms/callMeVariantOpcAtom';
import { opcInfoAtom } from '../../../../recoil/atoms/opcInfoAtom';
import { isOpcFetchingAtom } from '../../../../recoil/atoms/isOpcFetchingAtom';

const AdjustInsuranceSum = ({
    termInfo,
    updateTermValue
}: {
    termInfo: OPCTermDTO;
    updateTermValue: (termValue: number) => void;
}): JSX.Element => {
    const [showWholeNumberError, setShowWholeNumberError] = useState<boolean>(false);
    const [showValidationError, setShowValidationError] = useState<boolean>(false);
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const getHippoText = useHippoText();
    const setIsOpcDataFilled = useSetRecoilState(isOpcDataFilledAtom);
    const setInformationModalVariant = useSetRecoilState(infoModalVariantOpcAtom);
    const setCallMeVariantOpc = useSetRecoilState(callMeVariantOpcAtom);
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);
    const isAccidentDeathCovSumInsured = termInfo.publicId === ACC_DEATH_COVERAGE_TERM;
    const isInjuryTermSumInsured = termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM;
    const isChildInjurySumInsured =
        termInfo.publicId === CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM;
    const isAccidentDeathCoverageOrInjuryTerm =
        isAccidentDeathCovSumInsured || isInjuryTermSumInsured || isChildInjurySumInsured;
    const minimumValue = termInfo.valueMin ?? 0;
    const maximumValue = termInfo.valueMax ?? 0;

    const handleUpdateSum = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setShowValidationError(false);
        setShowWholeNumberError(false);
        const value = event.target.value.replace(' kr.', '').replaceAll('.', '');
        const currentInsuranceSum = Number(value);
        const isDeathCovOutsideRange =
            currentInsuranceSum < minimumValue && isAccidentDeathCoverageOrInjuryTerm;
        const isAccidentInsuredSumRoundedtoThousand =
            isAccidentDeathCoverageOrInjuryTerm && currentInsuranceSum % 1000 === 0;

        if (isDeathCovOutsideRange || !isAccidentInsuredSumRoundedtoThousand) {
            setIsOpcDataFilled(false);
            setShowWholeNumberError(!isAccidentInsuredSumRoundedtoThousand);
            setShowValidationError(isDeathCovOutsideRange);
            return;
        }

        if (
            !isFromDAFlow &&
            (isInjuryTermSumInsured || isChildInjurySumInsured) &&
            currentInsuranceSum < Number(termInfo.value)
        ) {
            setCallMeVariantOpc('lowerAccInsuranceSumThanPrevious');
            return;
        }

        if (
            (isAccidentDeathCovSumInsured || isInjuryTermSumInsured || isChildInjurySumInsured) &&
            currentInsuranceSum > maximumValue
        ) {
            setCallMeVariantOpc('deathCovExceedLimit');
            return;
        }

        updateTermValue(currentInsuranceSum);
        setIsOpcDataFilled(true);
    };

    return (
        <>
            <div className="u-spacing-stack-none">
                <h6>{getHippoText('DSS-COVERABLE-DETAILS-DEDUCTIBLE-DEATH-COVERAGE-SUBHEADER')}</h6>
                <NumberField
                    label="Indtast forsikringssum"
                    suffix=" kr."
                    withFormatting
                    onBlur={handleUpdateSum}
                    isValid={!showValidationError && !showWholeNumberError}
                    defaultValue={termInfo.value}
                    value={termInfo.value}
                    type="text"
                    errorText=""
                    onValueChange={(values) => {
                        if (values.value.length <= 0) setIsOpcDataFilled(false);
                    }}
                    disabled={isOpcFetching}
                />
                <div className="u-spacing-inset-bottom-6">
                    <p className="paragraph--small u-spacing-stack-none u-color-supplement-grey-dark">
                        {'Du kan vælge et beløb mellem ' +
                            getCurrencyString(minimumValue, '') +
                            ' - ' +
                            getCurrencyString(maximumValue, ' kr.')}
                    </p>
                    {showValidationError && (
                        <p className="paragraph--small u-spacing-stack-none u-color-supplement-error-base">
                            {getHippoText(
                                'DSS-COVERABLE-DETAILS-DEDUCTIBLE-DEATH-COVERAGE-VALIDATION-TEXT'
                            )}
                        </p>
                    )}
                    {showWholeNumberError && (
                        <p className="paragraph--small u-spacing-stack-none u-color-supplement-error-base">
                            {getHippoText('DSS-ACC-CHILD-CONDITIONS')}
                        </p>
                    )}
                </div>
                <div className="flos-field">
                    <Link
                        onClick={() => {
                            setInformationModalVariant('accReduceInsuranceSum');
                        }}
                        className="u-color-primary-2-base"
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-DEDUCTIBLE-DEATH-COVERAGE-INFO-MODAL')}
                    </Link>
                </div>
            </div>
        </>
    );
};

export default AdjustInsuranceSum;
